import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getOrderSession, lumpsumOrder, getSchemes } from "../../../api/bseApi";

const FOLIO_TYPES = [
  { value: "FRESH", label: "FRESH" },
  { value: "ADDITIONAL", label: "ADDITIONAL" },
];
const PAYMENT_METHODS = [
  { value: "P", label: "Physical (P)" },
  { value: "C", label: "CDSL (C)" },
  { value: "N", label: "NSDL (N)" },
];
const EUIN_DECLARATION = [
  { value: "Y", label: "Yes (Y)" },
  { value: "N", label: "No (N)" },
];

/**
 * LumpsumPurchaseScreen - BSE StAR MF Lumpsum Order Entry
 * Opens after ClientCode (UCC) is available
 * Receives: ClientCode, KYCStatus (Y), EUIN from state or userId
 */
export default function LumpsumPurchaseScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [context, setContext] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [form, setForm] = useState({
    schemeCd: "",
    schemeCdManual: "",
    amount: "",
    buySellType: "FRESH",
    folioNo: "",
    euin: "",
    euinVal: "N",
    dptxn: "P",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const state = location.state;

    if (state?.clientCode) {
      const ctx = {
        clientCode: state.clientCode,
        kycStatus: state.kycStatus || "Y",
        euin: state.euin || "",
      };
      setContext(ctx);
      setForm((f) => ({ ...f, euin: state.euin || f.euin }));
      setFetching(false);
      return;
    }

    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          const uccValid = ucc && String(ucc).trim() !== "" && String(ucc) !== "Reject";
          if (!uccValid) {
            Swal.fire("Error", "ClientCode (UCC) not available. Complete UCC creation first.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({
            clientCode: String(ucc).trim(),
            kycStatus: "Y",
            euin: "",
          });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }

    Swal.fire("Error", "Missing ClientCode. Open from User Listing or after UCC creation.", "error").then(() => navigate("/user"));
    setFetching(false);
  }, [location.state, userId, navigate]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const res = await getSchemes();
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setSchemes(res.data.data);
        }
      } catch (err) {
        console.warn("Scheme list load failed:", err.message);
      }
    };
    loadSchemes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    const schemeCd = form.schemeCd || form.schemeCdManual;
    if (!schemeCd?.trim()) err.schemeCd = "Scheme selection is mandatory";
    const amt = parseFloat(form.amount);
    if (!form.amount || amt <= 0) err.amount = "Amount must be greater than 0";
    if (form.buySellType === "ADDITIONAL" && !form.folioNo?.trim()) {
      err.folioNo = "Folio No is mandatory when Folio Type is ADDITIONAL";
    }
    const euin = (form.euin || context?.euin || "").trim();
    if (!euin) err.euin = "EUIN is mandatory";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const sessionRes = await getOrderSession();
      if (sessionRes.data?.status !== 1 || !sessionRes.data?.data) {
        const msg = sessionRes.data?.error || sessionRes.data?.message || "Session failed";
        Swal.fire("Error", msg, "error");
        setLoading(false);
        return;
      }

      const { encryptedPassword, passKey } = sessionRes.data.data;
      const schemeCd = (form.schemeCd || form.schemeCdManual || "").trim();
      const euin = (form.euin || context.euin || "").trim();

      const payload = {
        encryptedPassword,
        passKey,
        clientCode: context.clientCode,
        schemeCd,
        buySell: "P",
        buySellType: form.buySellType,
        dptxn: form.dptxn,
        amount: parseFloat(form.amount),
        kycStatus: context.kycStatus || "Y",
        folioNo: form.buySellType === "ADDITIONAL" ? form.folioNo.trim() : "",
        euin,
        euinVal: form.euinVal,
        allRedeem: "N",
        minRedeem: "N",
        dpc: "Y",
      };

      const res = await lumpsumOrder(payload);

      if (res.data?.status === 1) {
        const orderId = res.data?.data?.orderId || res.data?.data?.transNo;
        const bseFailed = res.data?.bseFailed === true;
        Swal.fire({
          icon: "success",
          title: "Order Placed",
          text: bseFailed
            ? (res.data?.message || "Order saved locally. BSE could not process; you may retry later.")
            : `Lumpsum order placed successfully. Order No: ${orderId || "N/A"}`,
        });
        navigate("/payment/mode", {
          state: { orderNo: orderId, clientCode: context.clientCode, amount: parseFloat(form.amount) },
        });
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "Order failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d?.message === "string" && d.message) ||
        (typeof d?.bseMessage === "string" && d.bseMessage) ||
        (typeof d?.error === "string" && d.error) ||
        err.message ||
        "Order failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const useManualScheme = schemes.length === 0;

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link> </li>
                <li className="breadcrumb-item active">Lumpsum Purchase</li>
              </ol>
            </nav>
            <h5>Lumpsum Purchase</h5>
            <p className="text-muted mb-0">
              Client Code: {context.clientCode} | KYC: {context.kycStatus}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Purchase Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Scheme *</label>
                  {useManualScheme ? (
                    <input
                      type="text"
                      className={`form-control ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCdManual"
                      value={form.schemeCdManual}
                      onChange={handleChange}
                      placeholder="Enter BSE Scheme Code"
                    />
                  ) : (
                    <select
                      className={`form-select ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCd"
                      value={form.schemeCd}
                      onChange={handleChange}
                    >
                      <option value="">Select Scheme</option>
                      {schemes.map((s) => (
                        <option key={s.schemeCd} value={s.schemeCd}>
                          {s.schemeName} ({s.schemeCd})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.schemeCd && <div className="invalid-feedback">{errors.schemeCd}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Amount (OrderVal) *</label>
                  <input
                    type="number"
                    className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    min="1"
                    step="0.01"
                  />
                  {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Folio Type</label>
                  <select
                    className="form-select"
                    name="buySellType"
                    value={form.buySellType}
                    onChange={handleChange}
                  >
                    {FOLIO_TYPES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                {form.buySellType === "ADDITIONAL" && (
                  <div className="col-md-6">
                    <label className="form-label">Folio No *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.folioNo ? "is-invalid" : ""}`}
                      name="folioNo"
                      value={form.folioNo}
                      onChange={handleChange}
                      placeholder="Enter Folio Number"
                    />
                    {errors.folioNo && <div className="invalid-feedback">{errors.folioNo}</div>}
                  </div>
                )}
                <div className="col-md-6">
                  <label className="form-label">EUIN *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.euin ? "is-invalid" : ""}`}
                    name="euin"
                    value={form.euin || context.euin}
                    onChange={handleChange}
                    placeholder="Enter EUIN"
                  />
                  {errors.euin && <div className="invalid-feedback">{errors.euin}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">EUIN Declaration</label>
                  <select
                    className="form-select"
                    name="euinVal"
                    value={form.euinVal}
                    onChange={handleChange}
                  >
                    {EUIN_DECLARATION.map((e) => (
                      <option key={e.value} value={e.value}>{e.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Payment Method (DPTxn)</label>
                  <select
                    className="form-select"
                    name="dptxn"
                    value={form.dptxn}
                    onChange={handleChange}
                  >
                    {PAYMENT_METHODS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Placing Order...
                    </>
                  ) : (
                    "Place Lumpsum Order"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
